import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { MdDelete, MdEdit, MdAdd, MdList, MdLockOpen, MdLock, MdMoreVert, MdPersonOutline, MdOpenInNew, MdAccountTree } from 'react-icons/md';
import { deactivateEmployee, activateEmployee, deleteEmployee } from 'redux/organization/employeeSlice';
import { Button, Confirmation } from 'components/common';

const EmployeeDetailHeader = ({ employee }) => {
  const [showDelete,   setShowDelete]   = useState(false);
  const [menuOpen,     setMenuOpen]     = useState(false);
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const employeeId = employee?.id;
  const activeEmp  = employee?.active || false;

  const handleActivate   = () => dispatch(activateEmployee(employeeId)).unwrap();
  const handleDeActivate = () => dispatch(deactivateEmployee(employeeId)).unwrap();
  const handleDelete     = async () => { dispatch(deleteEmployee(employeeId)); navigate('/organization/employees'); };

  return (
    <>
      <div className="tw-bg-white tw-rounded-2xl tw-border tw-border-slate-100 tw-shadow-sm tw-mb-4 tw-px-4 tw-py-3 tw-flex tw-items-center tw-justify-between">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="tw-inline-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded tw-border tw-border-slate-200 tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50 tw-transition-colors tw-cursor-pointer"
          aria-label="Go back"
        >
          <span className="tw-text-sm">←</span>
        </button>

        <div className="tw-flex tw-items-center tw-gap-1">
          <Button variant="secondary" size="sm" onClick={() => navigate('/organization/employees/new')}>
            <MdAdd size={14} /> New
          </Button>

          {activeEmp ? (
            <Button variant="warning" size="sm" onClick={handleDeActivate}>
              <MdLockOpen size={14} /> De-activate
            </Button>
          ) : (
            <Button variant="warning" size="sm" onClick={handleActivate}>
              <MdLock size={14} /> Activate
            </Button>
          )}

          {/* Opens in a new tab on purpose: checking someone's account is a
              side-trip, and losing the employee record you were reading is the
              main annoyance this shortcut exists to remove. */}
          {employee?.portal_user?.id && (
            <Button
              variant="secondary"
              size="sm"
              title={`Open ${employee.portal_user.name}'s portal account in a new tab`}
              onClick={() => window.open(`/authorization/users/${employee.portal_user.id}`, '_blank', 'noopener')}
            >
              <MdPersonOutline size={14} /> User account <MdOpenInNew size={11} />
            </Button>
          )}

          {/* Same tab: the chart is a different way of looking at this same
              person, not a side-trip, and it opens focused on them. */}
          <Button
            variant="secondary"
            size="sm"
            title="Show this employee in the organization chart"
            onClick={() => navigate(`/organization/employees/org-chart?focus=${employeeId}`)}
          >
            <MdAccountTree size={14} /> Org chart
          </Button>

          <Button variant="secondary" size="sm" onClick={() => navigate(`/organization/employees/${employeeId}/edit`, { state: employee })}>
            <MdEdit size={14} /> Edit
          </Button>

          <Button variant="secondary" size="sm" onClick={() => navigate('/organization/employees')}>
            <MdList size={14} /> List
          </Button>

          {/* More menu */}
          <div className="tw-relative">
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              onBlur={() => setTimeout(() => setMenuOpen(false), 150)}
              className="tw-inline-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded tw-border tw-border-slate-200 tw-bg-white tw-text-slate-500 hover:tw-bg-slate-50 tw-transition-colors tw-cursor-pointer"
              aria-label="More actions"
            >
              <MdMoreVert size={16} />
            </button>
            {menuOpen && (
              <div className="tw-absolute tw-right-0 tw-top-10 tw-z-50 tw-bg-white tw-rounded-lg tw-border tw-border-slate-100 tw-shadow-lg tw-py-1 tw-min-w-[140px]">
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); setShowDelete(true); }}
                  className="tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-sm tw-text-red-600 hover:tw-bg-red-50 tw-transition-colors tw-bg-transparent tw-border-0 tw-cursor-pointer"
                >
                  <MdDelete size={14} /> Delete
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <Confirmation
        show={showDelete}
        onCancel={() => setShowDelete(false)}
        onConfirm={handleDelete}
        title="Delete Employee"
        message="Are you sure you want to delete this employee? This action cannot be undone."
        type="danger"
        confirmText="Delete"
      />
    </>
  );
};

EmployeeDetailHeader.propTypes = {
  employee: PropTypes.shape({
    id:     PropTypes.number,
    active: PropTypes.bool,
    portal_user: PropTypes.shape({
      id:   PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    }),
  }),
};

export default EmployeeDetailHeader;
